import Big from 'big.js';
import { replaceDecimalSeparator } from 'helpers/strings';
import { isNullish } from 'helpers/objects';
import { getBalance } from './helpers';

Big.RM = 0;
const BIG_0 = Big(0);

export const parseBigValue = value => {
  const replaced = replaceDecimalSeparator(value);
  if (isNaN(replaced) || isNaN(parseFloat(replaced))) return BIG_0;
  return Big(replaced);
};

export const validateBaseSizeMinimum = (value, market) => {
  return Big(replaceDecimalSeparator(value)).gte(market.baseSizeMinimum);
};

export const validateTotalPrecision = (price, quantity, market) => {
  const total = Big(replaceDecimalSeparator(price)).times(Big(replaceDecimalSeparator(quantity)));
  return validateQuoteTotalPrecision(total, market);
};

export const validateQuoteTotalPrecision = (total, market) => {
  return Big(total).gte(market.quoteIncrement);
};

export const validateAmountPrecision = (value, precision) => {
  const formattedValue = replaceDecimalSeparator(value);
  if (formattedValue.includes('.')) {
    return formattedValue.split('.')[1].length <= precision ?? 2;
  }
  return true;
};

export const validateNumeric = value => {
  const stringValue = replaceDecimalSeparator(value);
  let validNumeric = !isNaN(stringValue);
  try {
    Big(stringValue);
  } catch (error) {
    validNumeric = false;
  }
  return validNumeric;
};

export const validateInsufficientFundsLimit = (price, quantity, side, market, balances, takerFee) => {
  const bigPrice = parseBigValue(price);
  const bigQty = parseBigValue(quantity);
  const newSubtotalValue = bigPrice.times(bigQty);
  let insufficientFunds = false;
  let asset;
  if (side === 'buy') {
    asset = market.quoteCurrency;
    const newFeeValue = newSubtotalValue.times(Big(takerFee).div(100));
    const newTotalValue = newSubtotalValue.plus(newFeeValue);
    const currentBalance = Big(getBalance(balances, asset));
    if (currentBalance.eq(0)) insufficientFunds = true;
    else insufficientFunds = newTotalValue.gt(currentBalance);
  } else {
    asset = market.baseCurrency;
    const currentBalance = getBalance(balances, asset);
    insufficientFunds = bigQty.gt(currentBalance);
  }
  return { insufficientFunds, asset };
};

export const validateInsufficientFundsMarket = (quantity, side, market, balances, marketPrice) => {
  const bigQty = parseBigValue(quantity);
  let insufficientFunds = false;
  let asset;
  if (side === 'buy') {
    asset = market.quoteCurrency;
    const currentBalance = Big(getBalance(balances, asset));
    if (isNullish(marketPrice)) insufficientFunds = false;
    else if (currentBalance.eq(0)) insufficientFunds = true;
    else insufficientFunds = bigQty.gt(currentBalance.div(marketPrice));
  } else {
    asset = market.baseCurrency;
    const currentBalance = getBalance(balances, asset);
    insufficientFunds = bigQty.gt(currentBalance);
  }
  return { insufficientFunds, asset };
};

export const validateCanFillOrder = (quantity = 0, orders = []) => {
  const totalQuantityAvailable = orders.reduce((total, order) => {
    return total.plus(Big(order.quantity));
  }, Big(0));
  return totalQuantityAvailable.gte(Big(quantity));
};
